import { ButtonLink } from '../../design/index.js';
import { SiteSection } from '../SiteSection.js';

/**
 * Trust, stated as facts we can back (§24).
 *
 * A register rather than a set of tiles: what we guarantee on the left, how,
 * on the right, as it would be written in a contract. Every line below describes something that is
 * implemented and tested in this repository — tenant isolation has a blocking
 * test suite, audio recording defaults to off, the assistant has no tool that
 * accepts a card number.
 *
 * What is **not** here matters as much: no certification badge, no "99,9 %",
 * no ISO logo, no SOC 2. We do not hold them, and a badge nobody audited is
 * the fastest way to lose the argument this section exists to win.
 */

const GUARANTEES: readonly (readonly [string, string])[] = [
  [
    'Chaque établissement est isolé',
    'La séparation est imposée par la base de données elle-même, pas par du code applicatif. Une suite de tests bloquante vérifie qu’un salon ne peut pas lire les données d’un autre.',
  ],
  [
    'Les données sensibles sont chiffrées',
    'Numéros et adresses sont chiffrés par enveloppe. La clé principale ne réside jamais dans la base.',
  ],
  [
    'Aucune carte bancaire dictée à l’assistante',
    'Il n’existe aucun outil qui accepterait un numéro de carte. Le règlement passe par un lien sécurisé, sur votre propre compte Stripe.',
  ],
  [
    'Enregistrement audio désactivé par défaut',
    'Et la conservation des transcriptions aussi. Si vous les activez, le message d’accueil doit l’annoncer — c’est votre obligation, et nous vous la rappelons.',
  ],
  [
    'Double authentification',
    'Sur les actions sensibles, et sur les comptes qui administrent l’établissement.',
  ],
  [
    'Journal d’audit',
    'Qui a fait quoi, quand. Consultable, et conservé indépendamment des transcriptions.',
  ],
];

export function Security(): React.JSX.Element {
  return (
    <SiteSection
      id="securite"
      title="Vos clients vous font confiance. Nous aussi."
      intro={
        <p>
          Un assistant qui répond au téléphone d’un salon manipule des noms, des numéros et des
          rendez-vous. Voici ce que cela nous impose.
        </p>
      }
    >
      <dl className="border-b border-wall-line">
        {GUARANTEES.map(([title, detail]) => (
          <div
            key={title}
            className="grid gap-x-8 gap-y-1 border-t border-wall-line py-5 md:grid-cols-[15rem_1fr]"
          >
            <dt className="text-ui font-medium text-ink">{title}</dt>
            <dd className="text-ui text-ink-muted">{detail}</dd>
          </div>
        ))}
      </dl>

      <p className="mt-6 max-w-2xl text-ui text-ink-muted">
        Nous n’affichons aucun label de certification que nous n’avons pas obtenu. Si vous avez
        besoin d’un engagement contractuel précis, demandez-le : nous répondrons par écrit.
      </p>

      <div className="mt-6">
        <ButtonLink href="#demo" variant="secondary">
          Parler de sécurité avec nous
        </ButtonLink>
      </div>
    </SiteSection>
  );
}
